import type { RouteRecordRaw } from 'vue-router'

/**
 * 路由表。
 *
 * 網址**不帶語系**。語系是使用者偏好，存在 localStorage，只從 header 的
 * 選單切換 —— 路徑帶語系（`/zh-CN/login`）只有在需要讓搜尋引擎分別索引
 * 各語言版本時才划算，這個站不需要，卻要付出每條路由多一個參數、
 * 每個連結都得帶 `params.lang` 的代價。
 *
 * 全部具名 —— 元件只用 `{ name }` 導航，永遠不拼路徑字串。
 */

declare module 'vue-router' {
  interface RouteMeta {
    /** 需要登入才能進入 */
    requiresAuth?: boolean
    /** 已登入就不該看到（登入頁、註冊頁） */
    guestOnly?: boolean
    /** i18n key，用來組頁面標題 */
    titleKey?: string
  }
}

export const routes: RouteRecordRaw[] = [
  {
    /**
     * 遊戲頁。**刻意掛在 `DefaultLayout` 外面**（頂層而非 children）——
     * 它只是一個載入畫面，換到網址就把自己換成遊戲商的頁面，
     * 外面套站台的 header 與底部導覽只會在那零點幾秒裡閃一下。
     *
     * 這一頁是被 `useGameLaunch.launch()` 開在**新分頁**的。
     *
     * 參數是遊戲商代碼與遊戲編號（目錄裡的 `productCode` / `gameId`），
     * 所以網址可以直接分享、登入後也能用 `redirect` 回到同一款。
     */
    path: '/game/:providerCode/:gameId',
    name: 'game',
    component: () => import('@/views/game/GameLaunchView.vue'),
    meta: { requiresAuth: true, titleKey: 'game.title' },
  },
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
        meta: { titleKey: 'home.title' },
      },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { guestOnly: true, titleKey: 'auth.loginTitle' },
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: { guestOnly: true, titleKey: 'auth.registerTitle' },
      },
      {
        // 網址用 `slots` / `card` / `fish` 這種代稱，不直接用資料庫的
        // `Slot` / `Card` —— 對照表在 `lib/gameCategories.ts`，
        // 認不得的代稱由畫面渲染 404
        path: 'games/:category',
        name: 'category',
        component: () => import('@/views/CategoryView.vue'),
      },
      {
        // 不加 `requiresAuth` —— 訪客的收藏存在 localStorage，一樣看得到。
        // 擋在登入牆後面會斷掉「先逛、先收藏、想清楚再註冊」這條動線
        path: 'favorites',
        name: 'favorites',
        component: () => import('@/views/FavoritesView.vue'),
        meta: { titleKey: 'favorites.title' },
      },
      {
        path: 'promotions',
        name: 'promotions',
        component: () => import('@/views/promotions/PromotionsView.vue'),
        meta: { titleKey: 'promotions.title' },
      },
      {
        // 詳情用 slug 而非數字 id —— 網址本身就說明了是哪一檔優惠，
        // 也讓資料表的 key 與網址是同一個值，不必再維護一張對照表
        path: 'promotions/:slug',
        name: 'promotion-detail',
        component: () => import('@/views/promotions/PromotionDetailView.vue'),
        meta: { titleKey: 'promotions.title' },
      },
      {
        path: 'events',
        name: 'events',
        component: () => import('@/views/events/EventsView.vue'),
        meta: { titleKey: 'events.title' },
      },
      {
        path: 'events/:slug',
        name: 'event-detail',
        component: () => import('@/views/events/EventDetailView.vue'),
        meta: { titleKey: 'events.title' },
      },
      {
        // 三支都是**帳號底下的錢**，一律要登入。網址刻意不掛在 `/account/` 下面：
        // header 的存款／取款鈕是站上最常按的兩顆，`/deposit` 比
        // `/account/deposit` 短，也讓客服能直接口述給玩家
        path: 'deposit',
        name: 'deposit',
        component: () => import('@/views/wallet/DepositView.vue'),
        meta: { requiresAuth: true, titleKey: 'wallet.depositTitle' },
      },
      {
        path: 'withdraw',
        name: 'withdraw',
        component: () => import('@/views/wallet/WithdrawView.vue'),
        meta: { requiresAuth: true, titleKey: 'wallet.withdrawTitle' },
      },
      {
        path: 'transactions',
        name: 'transactions',
        component: () => import('@/views/wallet/TransactionsView.vue'),
        meta: { requiresAuth: true, titleKey: 'wallet.transactions' },
      },
      {
        // 下注紀錄。資料來自 pocket 的 /game/report（`services/betReport.api.ts`）。
        // 那是彙總，一種玩法一列，沒有注單號也沒有下注時間
        path: 'bets',
        name: 'bets',
        component: () => import('@/views/bets/BetsView.vue'),
        meta: { requiresAuth: true, titleKey: 'bets.title' },
      },
      {
        path: 'account',
        name: 'account',
        component: () => import('@/views/AccountView.vue'),
        meta: { requiresAuth: true, titleKey: 'account.title' },
      },
      {
        // 沒有 catch-all 的話，未匹配路徑會靜默渲染空白頁 —— T1 就是這樣。
        // 放在 layout 底下，讓 404 頁仍有 header 與 footer 可供導航離開
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue'),
        meta: { titleKey: 'notFound.title' },
      },
    ],
  },
]
